import React, { useEffect, useState } from "react";
import SimpleAppointmentService from "../../appointments/SimpleAppointmentService";
import OfficeService from "../../office/officeService";

const DEFAULT_NOT_SURE_DOCTOR_ID = 15;

const SimplePatientAppointments = ({ patientId }) => {
  const [appointments, setAppointments] = useState([]);
  const [officeNames, setOfficeNames] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    officeId: "",
    appointmentDate: "",
    appointmentTime: "",
    notes: "",
    conformation: "Scheduled",
  });
  const [allTimeSlots, setAllTimeSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const simpleAppointmentService = new SimpleAppointmentService();
  const officeService = new OfficeService();

  const role = localStorage.getItem("role")?.replace("ROLE_", "") || "";
  const isAdmin = role === "ADMIN";

  const getAppointmentId = (appt) => {
    return appt.simpleAppointmentId || appt.simple_appointment_id;
  };

  const isCompleted = (appt) => {
    return appt?.conformation?.toLowerCase() === "completed";
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  useEffect(() => {
    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const loadAppointments = async () => {
    if (!patientId) return;

    try {
      setLoading(true);
      setError("");

      const result =
        await simpleAppointmentService.getSimpleAppointmentsByPatient(patientId);

      const list = Array.isArray(result) ? result : [];
      setAppointments(list);

      const officeMap = {};

      for (const appt of list) {
        if (appt.officeId && !officeMap[appt.officeId]) {
          try {
            const office = await officeService.getOfficeById(appt.officeId);
            officeMap[appt.officeId] = office.officeName;
          } catch {
            officeMap[appt.officeId] = `Office #${appt.officeId}`;
          }
        }
      }

      setOfficeNames(officeMap);
    } catch (err) {
      console.error("Failed to load simple appointments:", err);
      setError(err.message || "Could not load appointments.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTimes = async (officeId, date) => {
    if (!officeId || !date) {
      setAllTimeSlots([]);
      setBookedSlots([]);
      return;
    }

    try {
      setLoadingTimes(true);

      const response = await simpleAppointmentService.getAvailableTimes(
        officeId,
        date
      );

      setAllTimeSlots(response?.allSlots || []);
      setBookedSlots(response?.bookedSlots || []);
    } catch (err) {
      console.error("Failed to load time slots:", err);
      setAllTimeSlots([]);
      setBookedSlots([]);
      alert(err.message || "Could not load available times.");
    } finally {
      setLoadingTimes(false);
    }
  };

  const startEdit = async (appt) => {
    if (isCompleted(appt) && !isAdmin) {
      alert("Completed appointments are locked.");
      return;
    }

    const id = getAppointmentId(appt);

    setEditingId(id);

    const nextForm = {
      officeId: appt.officeId || "",
      appointmentDate: appt.appointmentDate || "",
      appointmentTime: appt.appointmentTime || "",
      notes: appt.notes || "",
      conformation: appt.conformation || "Scheduled",
    };

    setEditForm(nextForm);

    await fetchAvailableTimes(nextForm.officeId, nextForm.appointmentDate);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      officeId: "",
      appointmentDate: "",
      appointmentTime: "",
      notes: "",
      conformation: "Scheduled",
    });
    setAllTimeSlots([]);
    setBookedSlots([]);
  };

  const handleEditChange = async (key, value) => {
    setEditForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "appointmentDate" ? { appointmentTime: "" } : {}),
    }));

    if (key === "appointmentDate") {
      await fetchAvailableTimes(editForm.officeId, value);
    }
  };

  const handleTimeSelect = (time) => {
    setEditForm((prev) => ({
      ...prev,
      appointmentTime: time,
    }));
  };

  const saveEdit = async (appt) => {
    const appointmentId = getAppointmentId(appt);

    if (!editForm.officeId) {
      alert("Office is required.");
      return;
    }

    if (!editForm.appointmentDate) {
      alert("Date is required.");
      return;
    }

    if (!editForm.appointmentTime) {
      alert("Time is required.");
      return;
    }

    const finalData = {
      ...appt,
      patientId: parseInt(patientId, 10),
      officeId: parseInt(editForm.officeId, 10),
      doctorId: appt.doctorId || DEFAULT_NOT_SURE_DOCTOR_ID,
      appointmentDate: editForm.appointmentDate,
      appointmentTime: editForm.appointmentTime,
      notes: editForm.notes,
      conformation: editForm.conformation || appt.conformation || "Scheduled",
    };

    try {
      setSaving(true);
      setError("");

      const result = await simpleAppointmentService.updateSimpleAppointment(
        appointmentId,
        finalData
      );

      if (result?.success === false) {
        throw new Error(result?.message || "Could not update appointment.");
      }

      await loadAppointments();
      cancelEdit();
      showSuccess("Appointment updated successfully.");
    } catch (err) {
      console.error("Failed to update appointment:", err);
      alert(err.message || "Could not update appointment.");
    } finally {
      setSaving(false);
    }
  };

  const deleteAppointment = async (appt) => {
    const appointmentId = getAppointmentId(appt);

    if (isCompleted(appt) && !isAdmin) {
      alert("Completed appointments can only be deleted by ADMIN.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this appointment?"
    );

    if (!confirmed) return;

    try {
      setSaving(true);
      setError("");

      const result = await simpleAppointmentService.deleteSimpleAppointment(
        appointmentId
      );

      if (result?.success === false) {
        throw new Error(result?.message || "Could not delete appointment.");
      }

      await loadAppointments();
      showSuccess("Appointment deleted successfully.");
    } catch (err) {
      console.error("Failed to delete appointment:", err);
      alert(err.message || "Could not delete appointment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="info-box">
      <h3>Appointments</h3>

      {successMessage && (
        <div className="success-message">✅ {successMessage}</div>
      )}

      {loading && <p>Loading appointments...</p>}

      {error && <p className="error">{error}</p>}

      {!loading && appointments.length === 0 && <p>No appointments found.</p>}

      {appointments.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {appointments.map((appt) => {
            const id = getAppointmentId(appt);
            const completed = isCompleted(appt);
            const isEditing = editingId === id;
            const canEdit = !completed || isAdmin;
            const canDelete = !completed || isAdmin;

            return (
              <div
                key={id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  padding: "10px",
                }}
              >
                {!isEditing ? (
                  <>
                    <p>
                      <strong>Date:</strong> {appt.appointmentDate}
                    </p>

                    <p>
                      <strong>Time:</strong> {appt.appointmentTime}
                    </p>

                    <p>
                      <strong>Office:</strong>{" "}
                      {officeNames[appt.officeId] || `Office #${appt.officeId}`}
                    </p>

                    <p>
                      <strong>Status:</strong>{" "}
                      {appt.conformation || "Scheduled"}
                    </p>

                    {appt.notes && (
                      <p>
                        <strong>Notes:</strong> {appt.notes}
                      </p>
                    )}

                    {completed && (
                      <p style={{ fontWeight: "bold" }}>
                        This appointment is completed and locked.
                      </p>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        flexWrap: "wrap",
                        marginTop: "10px",
                      }}
                    >
                      {canEdit && (
                        <button
                          type="button"
                          className="edit-button"
                          disabled={saving}
                          onClick={() => startEdit(appt)}
                        >
                          Edit
                        </button>
                      )}

                      {canDelete && (
                        <button
                          type="button"
                          className="cancel-button"
                          disabled={saving}
                          onClick={() => deleteAppointment(appt)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <label>Date</label>
                    <input
                      type="date"
                      value={editForm.appointmentDate}
                      onChange={(e) =>
                        handleEditChange("appointmentDate", e.target.value)
                      }
                    />

                    <label>Available Time Slots</label>

                    {loadingTimes ? (
                      <p>Loading available times...</p>
                    ) : allTimeSlots.length === 0 ? (
                      <p>No available office hours for this date.</p>
                    ) : (
                      <div className="time-slot-list">
                        {allTimeSlots.map((time, index) => {
                          const isCurrentOriginalTime =
                            time === appt.appointmentTime &&
                            editForm.appointmentDate === appt.appointmentDate &&
                            parseInt(editForm.officeId, 10) ===
                              parseInt(appt.officeId, 10);

                          const isBooked =
                            bookedSlots.includes(time) && !isCurrentOriginalTime;

                          const isSelected = time === editForm.appointmentTime;

                          return (
                            <button
                              key={`${time}-${index}`}
                              type="button"
                              disabled={isBooked}
                              className={`${
                                isBooked ? "booked-slot" : "available-slot"
                              } ${isSelected ? "selected-slot" : ""}`}
                              onClick={() => !isBooked && handleTimeSelect(time)}
                            >
                              {time}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <label>Notes</label>
                    <textarea
                      value={editForm.notes}
                      onChange={(e) =>
                        handleEditChange("notes", e.target.value)
                      }
                      placeholder="Appointment notes"
                    />

                    {isAdmin && (
                      <>
                        <label>Status</label>
                        <select
                          value={editForm.conformation}
                          onChange={(e) =>
                            handleEditChange("conformation", e.target.value)
                          }
                        >
                          <option value="Scheduled">Scheduled</option>
                          <option value="Walked In">Walked In</option>
                          <option value="In Room">In Room</option>
                          <option value="Completed">Completed</option>
                          <option value="Canceled">Canceled</option>
                          <option value="No Show">No Show</option>
                        </select>
                      </>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        flexWrap: "wrap",
                        marginTop: "10px",
                      }}
                    >
                      <button
                        type="button"
                        className="save-button"
                        disabled={saving}
                        onClick={() => saveEdit(appt)}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>

                      <button
                        type="button"
                        className="cancel-button"
                        disabled={saving}
                        onClick={cancelEdit}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SimplePatientAppointments;